import { useState } from "react";
import { useTranslation } from "../../../i18n";
import {
  Play,
  Pause,
  RotateCcw,
  ChevronRight,
  Calendar,
  CreditCard,
  Clock,
  FileText,
  Eye,
  type LucideIcon,
} from "lucide-react";

interface FlowStep {
  id: number;
  title: string;
  description: string;
  details: string;
  icon: LucideIcon;
  color: string;
}

export default function BillingPaymentFlow() {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(1);
  const [isPlaying, setIsPlaying] = useState(false);

  const flowSteps: FlowStep[] = [
    {
      id: 1,
      title: t('routes.billing.paymentFlow.steps.0.title'),
      description: t('routes.billing.paymentFlow.steps.0.description'),
      details: t('routes.billing.paymentFlow.steps.0.details'),
      icon: Calendar,
      color: "bg-blue-500",
    },
    {
      id: 2,
      title: t('routes.billing.paymentFlow.steps.1.title'),
      description: t('routes.billing.paymentFlow.steps.1.description'),
      details: t('routes.billing.paymentFlow.steps.1.details'),
      icon: CreditCard,
      color: "bg-green-500",
    },
    {
      id: 3,
      title: t('routes.billing.paymentFlow.steps.2.title'),
      description: t('routes.billing.paymentFlow.steps.2.description'),
      details: t('routes.billing.paymentFlow.steps.2.details'),
      icon: Clock,
      color: "bg-amber-500",
    },
    {
      id: 4,
      title: t('routes.billing.paymentFlow.steps.3.title'),
      description: t('routes.billing.paymentFlow.steps.3.description'),
      details: t('routes.billing.paymentFlow.steps.3.details'),
      icon: FileText,
      color: "bg-purple-500",
    },
    {
      id: 5,
      title: t('routes.billing.paymentFlow.steps.4.title'),
      description: t('routes.billing.paymentFlow.steps.4.description'),
      details: t('routes.billing.paymentFlow.steps.4.details'),
      icon: Eye,
      color: "bg-indigo-500",
    },
  ];

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
    if (!isPlaying) {
      // Start animation
      const interval = setInterval(() => {
        setCurrentStep((prev) => {
          if (prev >= flowSteps.length) {
            setIsPlaying(false);
            clearInterval(interval);
            return 1;
          }
          return prev + 1;
        });
      }, 2000);
    }
  };

  const handleReset = () => {
    setCurrentStep(1);
    setIsPlaying(false);
  };

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {t('routes.billing.paymentFlow.title')}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            {t('routes.billing.paymentFlow.description')}
          </p>

          {/* Controls */}
          <div className="flex justify-center space-x-4 mb-8">
            <button
              onClick={handlePlayPause}
              className="inline-flex items-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              {isPlaying ? (
                <Pause className="h-5 w-5 mr-2" />
              ) : (
                <Play className="h-5 w-5 mr-2" />
              )}
              {isPlaying
                ? t('routes.billing.paymentFlow.pause')
                : t('routes.billing.paymentFlow.play')} Animation
            </button>
            <button
              onClick={handleReset}
              className="inline-flex items-center px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              <RotateCcw className="h-5 w-5 mr-2" />
              {t('routes.billing.paymentFlow.reset')}
            </button>
          </div>
        </div>

        {/* Flow Visualization */}
        <div className="relative">
          {/* Progress Line */}
          <div className="absolute top-16 left-0 right-0 h-1 bg-gray-200 rounded-full">
            <div
              className="h-full bg-gradient-to-r from-blue-500 to-green-500 rounded-full transition-all duration-1000"
              style={{ width: `${(currentStep / flowSteps.length) * 100}%` }}
            />
          </div>

          {/* Steps */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-8 relative">
            {flowSteps.map((step, index) => (
              <div key={step.id} className="relative">
                {/* Step Circle */}
                <div
                  className={`w-16 h-16 rounded-full flex items-center justify-center text-2xl mb-4 mx-auto transition-all duration-500 ${
                    currentStep >= step.id
                      ? `${step.color} text-white shadow-lg scale-110`
                      : "bg-gray-200 text-gray-400"
                  }`}
                >
                  <step.icon className="w-6 h-6" />
                </div>

                {/* Arrow */}
                {index < flowSteps.length - 1 && (
                  <div className="hidden md:block absolute top-8 -right-4 transform -translate-y-1/2">
                    <ChevronRight
                      className={`h-6 w-6 transition-colors ${
                        currentStep > step.id
                          ? "text-green-500"
                          : "text-gray-300"
                      }`}
                    />
                  </div>
                )}

                {/* Step Content */}
                <div
                  className={`text-center transition-all duration-500 ${
                    currentStep >= step.id
                      ? "opacity-100 transform translate-y-0"
                      : "opacity-50 transform translate-y-2"
                  }`}
                >
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {step.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    {step.description}
                  </p>

                  {/* Expanded Details */}
                  {currentStep === step.id && (
                    <div className="bg-gray-50 p-4 rounded-lg border-l-4 border-green-500 animate-fade-in">
                      <p className="text-sm text-gray-700">{step.details}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Technical Details */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
            <h4 className="text-lg font-semibold text-gray-900 mb-3">
              {t('routes.billing.paymentFlow.cards.0.title')}
            </h4>
            <p className="text-gray-600 text-sm">
              {t('routes.billing.paymentFlow.cards.0.description')}
            </p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
            <h4 className="text-lg font-semibold text-gray-900 mb-3">
              {t('routes.billing.paymentFlow.cards.1.title')}
            </h4>
            <p className="text-gray-600 text-sm">
              {t('routes.billing.paymentFlow.cards.1.description')}
            </p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
            <h4 className="text-lg font-semibold text-gray-900 mb-3">
              {t('routes.billing.paymentFlow.cards.2.title')}
            </h4>
            <p className="text-gray-600 text-sm">
              {t('routes.billing.paymentFlow.cards.2.description')}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
